import { makeStyles, tokens } from '@fluentui/react-components';
import { IPStack } from 'baseComponents';
import { usePendingApprovalsPoller } from 'components/Approvals/usePendingApprovalsPoller';
import AppSplash from 'components/Loading/AppSplash';
import ContentLoader from 'components/Loading/ContentLoader';
import SceneLoader from 'components/Loading/SceneLoader';
import NavRoot from 'components/NavMain/NavRoot';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import ProtectedRoute from 'components/Routing/ProtectedRoute';
import SignInRedirect from 'components/Routing/SignInRedirect';
import { useSignInReturn } from 'components/Routing/useSignInReturn';
import { useTenantLink } from 'components/Routing/useTenantLink';
import { globalNotAuthorizedURL, globalNotFoundURL, globalToastDelay } from 'globalConstants';
import { lazy, ReactNode, Suspense, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import AppContext from './AppContext';
import { ColorMode, UnifiedThemeProvider } from './AppDesignSystem';

//
// Lazy load all scenes (module splitting)
//
const Conversation = lazy(() => import('scenes/Conversation/Conversation'));
const History = lazy(() => import('scenes/History/History'));
const ExecutionDetail = lazy(() => import('scenes/History/ExecutionDetail'));
const SkillRepository = lazy(() => import('scenes/Skills/SkillRepository'));
const SkillDetail = lazy(() => import('scenes/Skills/SkillDetail'));
const SkillConversation = lazy(() => import('scenes/Skills/SkillConversation'));
const Approvals = lazy(() => import('scenes/Approvals/Approvals'));
const ApprovalDetail = lazy(() => import('scenes/Approvals/ApprovalDetail'));
const Welcome = lazy(() => import('scenes/Welcome/Welcome'));
const CreateOrganization = lazy(() => import('scenes/CreateOrganization/CreateOrganization'));
const Logout = lazy(() => import('scenes/Logout/Logout'));
const NotFound = lazy(() => import('scenes/NotFound/NotFound'));
const NotAuthorized = lazy(() => import('scenes/NotAuthorized/NotAuthorized'));
const AdminIndex = lazy(() => import('scenes/Admin/AdminIndex'));
const TenantAdminIndex = lazy(() => import('scenes/TenantAdmin/TenantAdminIndex'));
const NavBar = lazy(() => import('components/NavBar/NavBar'));
const MobileNavBar = lazy(() => import('components/NavBar/MobileNavBar'));

const useStyles = makeStyles({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  body: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  rail: {
    flexShrink: 0,
    height: '100%',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  scene: {
    flexGrow: 1,
    minHeight: 0,
    overflow: 'auto',
    position: 'relative',
  },
});

//
// Main app routing component
//
const AppRoutingContent = () => {
  const appContext = useContext(AppContext);
  const styles = useStyles();
  const login = appContext.user.login;
  //the badge count of the Approvals menu entry, once for every menu instance
  usePendingApprovalsPoller();
  //after a sign-in, the page a signed-out user opened (a mail link) before requireAuth sent them to the root
  useSignInReturn();
  //a mail link that names another organization of the user (?tenant=) switches to it before its page opens
  const isTenantLinkSwitching = useTenantLink();
  //the splash covers the app until the first sign-in check has an answer, so the chrome never pops in around a
  //spinner; a later check (a switch of organization) shows the scene loader
  const [isFirstLoadSettled, setIsFirstLoadSettled] = useState<boolean>(false);
  //the open routes never waited for the sign-in check, so the splash does not cover them either
  const { pathname } = useLocation();
  const isOpenRoute = pathname === '/logout' || pathname === globalNotFoundURL || pathname === globalNotAuthorizedURL;

  useEffect(() => {
    if (!appContext.isAuthInProgress && !isTenantLinkSwitching) setIsFirstLoadSettled(true);
  }, [appContext.isAuthInProgress, isTenantLinkSwitching]);

  //the page background behind the app follows the color mode, as the static splash of index.html set it
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', appContext.useDarkMode ? 'dark' : 'light');
  }, [appContext.useDarkMode]);

  const getTopNavbar = () => {
    const navBarProps = {
      login: appContext.login,
      logout: appContext.logout,
      switchOrg: appContext.switchOrg,
      switchOrgUnit: appContext.switchOrgUnit,
    };

    return appContext.isMobileView ? <MobileNavBar {...navBarProps} /> : <NavBar {...navBarProps} />;
  };

  //the landing at the root: the first scene for a licensed user, the Backup page for an admin while a backup
  //restore holds the organization, the creation of an organization for a signed-in user whose directory has none
  //yet, the welcome screen for everyone else
  const getStartElement = (): ReactNode => {
    if (appContext.isAuthenticated && login.isHeldByRestore() && hasUserFeature(appContext, FeatureTypes.Admin)) {
      return <Navigate to="/admin/backup" replace />;
    }

    if (appContext.isAuthenticated && login.tenantLicensed && login.userLicensed) {
      return (
        <ProtectedRoute featureType={FeatureTypes.Dashboard}>
          <Conversation />
        </ProtectedRoute>
      );
    }

    if (appContext.isAuthenticated && !login.tenantLicensed && !login.tenantId && login.userId) {
      return <CreateOrganization />;
    }

    return <Welcome />;
  };

  //after the open routes every route needs a signed-in user; anyone else goes to the root to sign in, and the page
  //they opened is kept for after the sign-in
  const requireAuth = (element: ReactNode): ReactNode => (appContext.isAuthenticated ? element : <SignInRedirect />);

  return (
    <UnifiedThemeProvider mode={appContext.useDarkMode ? ColorMode.Dark : ColorMode.Light}>
      <IPStack className={styles.shell}>
        {/* Top bar */}
        <Suspense fallback={null}>{getTopNavbar()}</Suspense>

        {/* Body */}
        <IPStack horizontal className={styles.body}>
          {/* Left side bar */}
          {!appContext.isMobileView && (
            <IPStack.Item className={styles.rail}>
              <NavRoot />
            </IPStack.Item>
          )}

          {/* Content area */}
          <IPStack className={styles.content}>
            {/* Notification area */}
            <ContentLoader />
            <ErrorMessage error={appContext.error} setError={appContext.setError} />
            <ToastContainer position="top-right" autoClose={globalToastDelay} containerId="app" />
            <ToastContainer position="bottom-center" autoClose={globalToastDelay} containerId="app-bottom" />

            {/* Scene area */}
            <IPStack.Item grow shrink className={styles.scene}>
              <Suspense fallback={<SceneLoader />}>
                <Routes>
                  {/* 1. All users may navigate to the open routes, signed in or not
                      2. While a sign-in or the organization switch of a mail link is in progress every other route
                         shows the loader
                      3. A protected route without a signed-in user goes to the root
                      4. A route checks the feature itself and goes to not-authorized
                      5. Anything else is not found */}
                  <Route path="/logout" element={<Logout />} />
                  <Route path={globalNotFoundURL} element={<NotFound />} />
                  <Route path={globalNotAuthorizedURL} element={<NotAuthorized />} />

                  {appContext.isAuthInProgress || isTenantLinkSwitching ? (
                    <Route path="*" element={<SceneLoader />} />
                  ) : (
                    <>
                      <Route path="/" element={getStartElement()} />
                      <Route
                        path="/history"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <History />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/history/:executionId"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <ExecutionDetail />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/skills"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <SkillRepository />
                          </ProtectedRoute>,
                        )}
                      />
                      {/* a Skill written (/skills/conversation) or changed in a conversation (phase 21 W7); the static
                          segment ranks above the Skill detail's :skillId */}
                      <Route
                        path="/skills/conversation"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.SkillOwner}>
                            <SkillConversation />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/skills/:skillId/conversation"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.SkillOwner}>
                            <SkillConversation />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/skills/:skillId/*"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <SkillDetail />
                          </ProtectedRoute>,
                        )}
                      />
                      {/* /approvals/requested is a tab of the queue; any other segment is the id of one approval (the
                          target of the approval mail) */}
                      <Route
                        path="/approvals"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <Approvals />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/approvals/requested"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <Approvals />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/approvals/:approvalRequestId"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Dashboard}>
                            <ApprovalDetail />
                          </ProtectedRoute>,
                        )}
                      />
                      {/* Policies, Audit and Users also open to a Security Reviewer (read-only); the rest of /admin/* needs Admin */}
                      <Route
                        path="/admin/policies"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.PolicyReader}>
                            <AdminIndex />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/admin/audit"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.AuditReader}>
                            <AdminIndex />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/admin/users"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.UserReader}>
                            <AdminIndex />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/admin/*"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.Admin} allowTenantRestoreHold>
                            <AdminIndex />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route
                        path="/tenant/*"
                        element={requireAuth(
                          <ProtectedRoute featureType={FeatureTypes.TenantAdmin}>
                            <TenantAdminIndex />
                          </ProtectedRoute>,
                        )}
                      />
                      <Route path="*" element={<Navigate to={globalNotFoundURL} replace />} />
                    </>
                  )}
                </Routes>
              </Suspense>
            </IPStack.Item>
          </IPStack>
        </IPStack>
      </IPStack>
      <AppSplash visible={!isFirstLoadSettled && !isOpenRoute} dark={appContext.useDarkMode} />
    </UnifiedThemeProvider>
  );
};

const AppRouting = () => {
  return (
    <BrowserRouter>
      <AppRoutingContent />
    </BrowserRouter>
  );
};

export default AppRouting;
